import {useMutation, useQueryClient} from "@tanstack/react-query";
import {message} from "antd";
import {AxiosError} from "axios";
import {PASSWORD_CHANGE_SESSION_EXPIRED_MESSAGE} from "../components/settings/password-update-form/password-change.constants";
import {AnalyticsEvent} from "../utils/analytics";
import {useClientV2} from "../utils/hooks";

declare const window: Window &
  typeof globalThis & {
    Intercom: any;
  };

const useSendEmailChangeOtp = () => {
  const client = useClientV2();
  return useMutation({
    mutationFn: () =>
      client(`settings/change-email`, {
        method: "GET",
      }),
    onError: (error: AxiosError<{message: string}>) => {
      message.error(error.message || "Failed to send OTP");
      AnalyticsEvent("Email update", "OTP send fail");
    },
    onSuccess: () => {
      message.success("An OTP has been sent to your new email address");
    },
  });
};

const useConfirmEmailChange = () => {
  const queryClient = useQueryClient();
  const client = useClientV2();
  return useMutation({
    mutationFn: (payload: {otpCode: string; email: string}) =>
      client(`settings/change-email/confirm`, {
        method: "PATCH",
        data: payload,
      }),
    onError: (error: AxiosError<{message: string}>) => {
      message.error(error.message || "Failed to confirm email change");
      AnalyticsEvent("Email update", "Email update fail");
    },
    onSuccess: (data) => {
      message.success("Email updated successfully");
      AnalyticsEvent("Email update", "Email update success");
      if (data?.data?.user?.email) {
        window.Intercom("update", {
          email: `${data.data.user.email}`,
        });
      }
      queryClient.invalidateQueries({queryKey: ["profile"]});
    },
  });
};

type TSettingsApiError = {message?: string};

const useVerifyPasswordChangeOtp = () => {
  const client = useClientV2();
  return useMutation({
    mutationFn: (otpCode: string) =>
      client(`settings/change-password/verify`, {
        method: "POST",
        data: {otpCode},
      }),
    onError: (error: TSettingsApiError) => {
      message.error(error.message ?? "Invalid OTP code");
      AnalyticsEvent("Password update", "OTP verify fail");
    },
    onSuccess: () => {
      message.success("OTP verified. You can now set your new password");
      AnalyticsEvent("Password update", "OTP verify success");
    },
  });
};

const useSendPasswordChangeOtp = () => {
  const client = useClientV2();
  return useMutation({
    mutationFn: (currentPassword: string) =>
      client(`settings/change-password`, {
        method: "POST",
        data: {currentPassword},
      }),
    onError: (error: AxiosError<{message: string}>) => {
      message.error(error.message || "Failed to send OTP");
      AnalyticsEvent("Password update", "OTP send fail");
    },
    onSuccess: () => {
      message.success("An OTP has been sent to your email address");
    },
  });
};

const useConfirmPasswordChange = () => {
  const queryClient = useQueryClient();
  const client = useClientV2();
  return useMutation({
    mutationFn: (payload: {password: string; confirmPassword: string}) =>
      client(`settings/change-password/confirm`, {
        method: "PATCH",
        data: payload,
      }),
    onError: (error: TSettingsApiError) => {
      if (error.message === PASSWORD_CHANGE_SESSION_EXPIRED_MESSAGE) {
        AnalyticsEvent("Password update", "Password update fail");
        return;
      }
      message.error(error.message ?? "Failed to confirm password change");
      AnalyticsEvent("Password update", "Password update fail");
    },
    onSuccess: () => {
      message.success("Password updated successfully");
      AnalyticsEvent("Password update", "Password update success");
      queryClient.invalidateQueries({queryKey: ["profile"]});
    },
  });
};

export {
  useSendEmailChangeOtp,
  useConfirmEmailChange,
  useSendPasswordChangeOtp,
  useVerifyPasswordChangeOtp,
  useConfirmPasswordChange,
};
